import React, { useState } from 'react'
import { MdCheckCircle } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import LoginHelpTrigger from '@/components/auth/login/LoginHelpTrigger'
import { validators } from '@/utils/validators'
import type { Identifier } from '@/hooks/useLoginFlow'
import type { ValidationErrors } from '@/types'

/**
 * Stage 1 of the add-email flow. Customer is in Dynamics by customer
 * number but the contact has no email; we collect an email here and
 * the backend emails them a 6-digit verification code. They land on
 * the verify step (LoginAddEmailVerifyStep) to enter the code + a
 * chosen password. On success the backend PATCHes Dynamics with the
 * email and creates the portal account in one go.
 */

interface Props {
  identifier: Identifier
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onSubmit: (email: string) => void
  onBack: () => void
}

function LoginAddEmailStep({
  identifier,
  loading,
  apiError,
  fieldErrors,
  onSubmit,
  onBack,
}: Props): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<ValidationErrors>({})

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const next: ValidationErrors = {}
    if (!email) {
      next.email = 'Email is required'
    } else if (!validators.isValidEmail(email)) {
      next.email = 'Please enter a valid email'
    }
    setErrors(next)
    if (Object.keys(next).length === 0) onSubmit(email)
  }

  return (
    <div className="login-step login-step--success">
      {/* A green tick, not the old amber warning triangle: finding the
          account is good news, and the exclamation made it read as an urgent
          error (FEDEV-540). */}
      <div className="login-step-icon login-step-icon--success">
        <MdCheckCircle size={48} />
      </div>
      <h1 className="login-step-heading">We need your email address</h1>
      <p className="login-step-subheading">
        We found your account, but there&rsquo;s no email address linked to
        it. Enter your email below and we&rsquo;ll send you a 6-digit code to
        verify your identity.
      </p>

      <ValidationSummary errors={fieldErrors} message={apiError} />

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="addEmail" className="form-label">
            Email address
          </label>
          <input
            id="addEmail"
            type="email"
            className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''}`}
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          {errors.email && (
            <span className="form-text is-invalid">{errors.email}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={loading}
        >
          {loading ? 'Sending code…' : 'Send code'}
        </button>
      </form>

      <div className="login-step-footer">
        <button type="button" className="btn btn-link" onClick={onBack}>
          Back
        </button>
        <LoginHelpTrigger identifier={identifier} />
      </div>
    </div>
  )
}

export default LoginAddEmailStep
